import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { getApiUrl } from "../config";
import { getAuthToken } from "../utils/tokenStorage";

interface Bank {
	code: string;
	name: string;
	kana: string;
	hira: string;
	roma: string;
}

interface Branch {
	code: string;
	name: string;
	kana: string;
	hira: string;
	roma: string;
}

interface BankBranchSearchProps {
	onBankSelect: (bank: Bank) => void;
	onBranchSelect: (branch: Branch) => void;
	selectedBankCode?: string;
	selectedBankName?: string;
	selectedBranchCode?: string;
	selectedBranchName?: string;
}

const BankBranchSearch: React.FC<BankBranchSearchProps> = ({
	onBankSelect,
	onBranchSelect,
	selectedBankCode,
	selectedBankName,
	selectedBranchCode,
	selectedBranchName,
}) => {
	const [bankSearch, setBankSearch] = useState("");
	const [branchSearch, setBranchSearch] = useState("");
	const [banks, setBanks] = useState<Bank[]>([]);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [showBankDropdown, setShowBankDropdown] = useState(false);
	const [showBranchDropdown, setShowBranchDropdown] = useState(false);
	const [isLoadingBanks, setIsLoadingBanks] = useState(false);
	const [isLoadingBranches, setIsLoadingBranches] = useState(false);

	const bankSearchRef = useRef<HTMLDivElement>(null);
	const branchSearchRef = useRef<HTMLDivElement>(null);

	// Close dropdowns when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				bankSearchRef.current &&
				!bankSearchRef.current.contains(event.target as Node)
			) {
				setShowBankDropdown(false);
			}
			if (
				branchSearchRef.current &&
				!branchSearchRef.current.contains(event.target as Node)
			) {
				setShowBranchDropdown(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Search banks
	const searchBanks = useCallback(async (query: string) => {
		if (query.length < 1) {
			setBanks([]);
			return;
		}

		setIsLoadingBanks(true);
		try {
			const token = getAuthToken();
			const response = await axios.get(`${getApiUrl()}/banks`, {
				params: { search: query, limit: 20 },
				headers: token ? { Authorization: `Bearer ${token}` } : {},
			});
			setBanks(response.data.banks || []);
		} catch (error) {
			console.error("Failed to search banks:", error);
			setBanks([]);
		} finally {
			setIsLoadingBanks(false);
		}
	}, []);

	// Search branches
	const searchBranches = useCallback(
		async (bankCode: string, query: string) => {
			if (!bankCode || query.length < 1) {
				setBranches([]);
				return;
			}

			setIsLoadingBranches(true);
			try {
				const token = getAuthToken();
				const response = await axios.get(
					`${getApiUrl()}/banks/${bankCode}/branches`,
					{
						params: { search: query, limit: 50 },
						headers: token ? { Authorization: `Bearer ${token}` } : {},
					}
				);
				setBranches(response.data.branches || []);
			} catch (error) {
				console.error("Failed to search branches:", error);
				setBranches([]);
			} finally {
				setIsLoadingBranches(false);
			}
		},
		[]
	);

	// Debounce bank search
	useEffect(() => {
		const timer = setTimeout(() => {
			searchBanks(bankSearch);
		}, 300);

		return () => clearTimeout(timer);
	}, [bankSearch, searchBanks]);

	// Debounce branch search
	useEffect(() => {
		const timer = setTimeout(() => {
			if (selectedBankCode) {
				searchBranches(selectedBankCode, branchSearch);
			}
		}, 300);

		return () => clearTimeout(timer);
	}, [branchSearch, selectedBankCode, searchBranches]);

	const handleBankSelect = (bank: Bank) => {
		onBankSelect(bank);
		setBankSearch(bank.name);
		setShowBankDropdown(false);
		setBranchSearch(""); // Reset branch search
		setBranches([]);
	};

	const handleBranchSelect = (branch: Branch) => {
		onBranchSelect(branch);
		setBranchSearch(branch.name);
		setShowBranchDropdown(false);
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			{/* Bank Search */}
			<div ref={bankSearchRef} className="relative">
				<label
					htmlFor="bank-search"
					className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2"
				>
					金融機関名 <span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<div className="relative">
					<input
						type="text"
						id="bank-search"
						value={selectedBankName || bankSearch}
						onChange={(e) => {
							setBankSearch(e.target.value);
							setShowBankDropdown(true);
						}}
						onFocus={() => setShowBankDropdown(true)}
						placeholder="銀行名を検索..."
						className="w-full px-3 sm:px-4 py-2 pr-10 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
					/>
					<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
						{isLoadingBanks ? (
							<div className="animate-spin h-5 w-5 border-2 border-violet-500 border-t-transparent rounded-full" />
						) : (
							<svg
								className="h-5 w-5 text-gray-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
								/>
							</svg>
						)}
					</div>
				</div>
				{selectedBankCode && (
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						金融機関コード: {selectedBankCode}
					</p>
				)}
				{showBankDropdown && banks.length > 0 && (
					<div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
						{banks.map((bank) => (
							<button
								key={bank.code}
								onClick={() => handleBankSelect(bank)}
								className="w-full text-left px-3 sm:px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white transition-colors"
							>
								<div className="font-medium">{bank.name}</div>
								<div className="text-xs text-gray-500 dark:text-gray-400">
									{bank.kana} ({bank.code})
								</div>
							</button>
						))}
					</div>
				)}
			</div>

			{/* Branch Search */}
			<div ref={branchSearchRef} className="relative">
				<label
					htmlFor="branch-search"
					className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2"
				>
					支店名 <span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<div className="relative">
					<input
						type="text"
						id="branch-search"
						value={selectedBranchName || branchSearch}
						onChange={(e) => {
							setBranchSearch(e.target.value);
							setShowBranchDropdown(true);
						}}
						onFocus={() => setShowBranchDropdown(true)}
						disabled={!selectedBankCode}
						placeholder={
							selectedBankCode
								? "支店名を検索..."
								: "先に銀行を選択してください"
						}
						className="w-full px-3 sm:px-4 py-2 pr-10 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
					/>
					<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
						{isLoadingBranches ? (
							<div className="animate-spin h-5 w-5 border-2 border-violet-500 border-t-transparent rounded-full" />
						) : (
							<svg
								className="h-5 w-5 text-gray-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
								/>
							</svg>
						)}
					</div>
				</div>
				{selectedBranchCode && (
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						支店コード: {selectedBranchCode}
					</p>
				)}
				{showBranchDropdown && branches.length > 0 && (
					<div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
						{branches.map((branch) => (
							<button
								key={branch.code}
								onClick={() => handleBranchSelect(branch)}
								className="w-full text-left px-3 sm:px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white transition-colors"
							>
								<div className="font-medium">{branch.name}</div>
								<div className="text-xs text-gray-500 dark:text-gray-400">
									{branch.kana} ({branch.code})
								</div>
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default BankBranchSearch;
